// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { type ImportEntry, useImportsListQuery } from "@/api/hooks/use-imports";
import { ImportPlusIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ImportCsvDialog } from "@/features/imports/import-csv-dialog";
import { ImportDataDialog } from "@/features/imports/import-data-dialog";
import { ImportsTable } from "@/features/imports/imports-table";
import { ImportsToolsMenu } from "@/features/imports/imports-tools-menu";
import { ManualSftpImportDialog } from "@/features/imports/manual-sftp-import-dialog";
import { useImportsFiltersState } from "@/features/imports/use-imports-filters-state";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { formatDuration, formatIsoDateTime } from "@/lib/format";

interface StoreImportsTabProps {
  storeName: string;
  // Both are needed for a manual SFTP replay: the run is filed against a store id, and the
  // location catalogue is scoped by chain.
  storeId?: number;
  chainName?: string;
  initialFilters?: string;
}

export function StoreImportsTab({ storeName, storeId, chainName, initialFilters }: StoreImportsTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.ingressImportsEdit);

  const [importDataOpen, setImportDataOpen] = useState(false);
  const [importCsvOpen, setImportCsvOpen] = useState(false);
  const [manualSftpOpen, setManualSftpOpen] = useState(false);

  const onSync = useCallback(
    (encoded: string | null) => {
      navigate({
        to: "/stores/$storeName",
        params: { storeName },
        search: { t: "imports", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, storeName],
  );

  const filters = useImportsFiltersState(initialFilters, onSync);
  const {
    pagination,
    setPagination,
    sorting,
    setSorting,
    search,
    setSearch,
    importTypeFilter,
    setImportTypeFilter,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,
    resetToFirstPage,
  } = filters;

  const { data, isLoading, refetch, isFetching } = useImportsListQuery({
    storeName,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    sortBy: "id",
    orderBy: "desc",
    search,
    importType: importTypeFilter,
    source: sourceFilter,
    status: statusFilter,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/ingress/imports", {
      params: {
        query: {
          page: 1,
          limit: -1,
          sortBy: "id",
          orderBy: "desc",
          "filter[storeName]": storeName,
        },
      },
    });
    const items = (res.data as unknown as { imports: ImportEntry[] }).imports;
    downloadCsv(
      `store-${storeName}-imports.csv`,
      ["ID", "Type", "Source", "Status", "Uploaded By", "Processing Time", "Created"],
      items.map((i) => [
        String(i.importRequestId),
        i.importType,
        i.source ?? "api",
        i.status ?? "",
        String(i.createdBy ?? ""),
        i.processingTime != null ? formatDuration(i.processingTime) : "",
        formatIsoDateTime(i.createdAt),
      ]),
    );
  }, [storeName]);

  return (
    <div className="p-6">
      <ImportsTable
        scope="store"
        storageKey="store-imports"
        imports={data?.imports ?? []}
        count={data?.count ?? 0}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refetch}
        users={data?.users}
        vendors={data?.vendors}
        canEdit={canEdit}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          resetToFirstPage();
        }}
        importTypeFilter={importTypeFilter}
        onImportTypeFilterChange={(value) => {
          setImportTypeFilter(value);
          resetToFirstPage();
        }}
        sourceFilter={sourceFilter}
        onSourceFilterChange={(value) => {
          setSourceFilter(value);
          resetToFirstPage();
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={(value) => {
          setStatusFilter(value);
          resetToFirstPage();
        }}
        onExport={handleExport}
        toolbar={
          <>
            <ImportsToolsMenu>
              {/* Plain CSV upload — no transformation involved, the file already matches our fields. */}
              <DropdownMenuItem onSelect={() => setImportCsvOpen(true)} disabled={!canEdit}>
                {t("imports.importCsv")}
              </DropdownMenuItem>
              {/* Needs both ids to file a store-scoped replay — without them the dialog could only
                  offer a chain-wide one, which is not what a store page should be queueing. */}
              <DropdownMenuItem onSelect={() => setManualSftpOpen(true)} disabled={storeId == null || !chainName}>
                {t("imports.manualSftp.menuItem")}
              </DropdownMenuItem>
            </ImportsToolsMenu>
            <Button onClick={() => setImportDataOpen(true)} disabled={!canEdit}>
              <ImportPlusIcon className="h-4 w-4" />
              {t("imports.importData")}
            </Button>
          </>
        }
      />
      <ImportDataDialog open={importDataOpen} onOpenChange={setImportDataOpen} storeName={storeName} />
      <ImportCsvDialog open={importCsvOpen} onOpenChange={setImportCsvOpen} storeName={storeName} />
      {storeId != null && chainName && (
        <ManualSftpImportDialog
          open={manualSftpOpen}
          onOpenChange={setManualSftpOpen}
          store={{ storeId, storeName, chainName }}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
